import { LinearProgress } from '@mui/material';
import { DatagridField } from '@opendoor/mosaic';
import moment from 'moment';

import Link from '../Link';
import AcceptReject from './components/AcceptReject';
import CountDown from './components/CountDown';
import MarkWorkOrderStarted from './components/MarkWorkOrderStarted';
import ShouldReIndex from './components/ShouldReIndex';
import Utilities from './components/Utilities';

const DATE_FORMAT = 'MM/DD/YYYY';

const USDollarFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const workOrdersFields: DatagridField[] = [
  {
    name: 'id',
    type: 'string',
    isPrimaryKey: true,
    hidden: true,
    renderer: (value: any) => value.id,
  },
  {
    name: 'address',
    type: 'string',
    colWidth: 200,
    renderer: (value: any) => (
      <Link
        isExternal
        target="_blank"
        href={`/vendor/work-orders/${value.id}/general`}
        label={
          <div id="address">
            <div className="blue4 fs-6">{value.address.street}</div>
            <div className="gray4 fs-8">
              {value.address.city}, {value.address.state} {value.address.postalCode}
            </div>
          </div>
        }
      />
    ),
  },
  {
    name: '',
    type: 'string',
    renderer: (value: any) => (
      <div className="flex flex-column">
        <Link
          isExternal
          target="_blank"
          href={`/vendor/work-orders/${value.id}/line-items`}
          label={`Line Items (${value.lineItemIds.length})`}
        />
        <Link
          isExternal
          target="_blank"
          href={`/vendor/work-orders/${value.id}/photos`}
          label="Photos"
        />
      </div>
    ),
  },
  {
    name: 'type',
    type: 'string',
    renderer: (value: any) => value.kind,
  },
  {
    name: 'countdown',
    type: 'number',
    renderer: (value: any) => (
      <CountDown
        progress={value.progress}
        dateRequestedAt={value.dateRequestedAt}
        scheduledStartAt={value.scheduledStartAt}
        scheduledStopAt={value.scheduledStopAt}
      />
    ),
  },
  {
    name: 'date requested',
    type: 'string',
    renderer: (value: any) => moment(value.dateRequestedAt).utc().format(DATE_FORMAT),
  },
  {
    name: 'start date',
    type: 'string',
    renderer: (value: any) => moment(value.scheduledStartAt).utc().format(DATE_FORMAT),
  },
  {
    name: 'end date',
    type: 'string',
    renderer: (value: any) => moment(value.scheduledStopAt).utc().format(DATE_FORMAT),
  },
  {
    name: 'progress',
    type: 'number',
    hidden: true,
    renderer: (value: any) => {
      let progressObject = value.progressObject;
      const progressVal = progressObject?.totalLineItems
        ? (progressObject?.numCompletedLineItems * 100) / progressObject?.totalLineItems
        : 100;

      return (
        progressObject && (
          <div className="pr-4 pt-2">
            <LinearProgress
              variant="determinate"
              sx={{ padding: 0.5, borderRadius: 1 }}
              value={progressVal}
            />
            <p>
              {progressObject?.numCompletedLineItems} of {progressObject?.totalLineItems}
            </p>
          </div>
        )
      );
    },
  },
  {
    name: 'budget',
    colWidth: 100,
    type: 'string',
    renderer: (value: any) => USDollarFormatter.format(value.acceptedCostCents / 100),
  },
  {
    name: 'utilities',
    type: 'string',
    renderer: (value: any) => (
      <div className="flex">
        <Utilities utilities={value.utilities} />
      </div>
    ),
  },
  {
    name: 'contact',
    type: 'string',
    renderer: (value: any) => (
      <>
        <p className="semi-bold">{value.managerContact?.fullName}</p>
        <p>{value.managerContact?.primaryPhone}</p>
      </>
    ),
  },
  {
    name: 'action',
    type: 'string',
    headerLabel: 'Action',
    hidden: true,
    renderer: (value: any) => {
      switch (value.progress) {
        case 'NEW':
          return <AcceptReject workOrderID={value.id} />;
        case 'READY_TO_START':
          return <MarkWorkOrderStarted workOrderID={value.id} />;
        default:
          return null;
      }
    },
  },
  {
    name: 'should_reindex',
    type: 'string',
    hidden: true,
    renderer: (value: any) => (
      <ShouldReIndex id={value.id} startDate={value.scheduledStartAt} progress={value.progress} />
    ),
  },
  {
    name: 'blank',
    headerLabel: '-',
    type: 'string',
    hidden: true,
    renderer: () => <></>,
  },
];

interface SortOptionsType {
  handleSortScheduledStartAt: () => void;
  handleSortScheduledStopAt: () => void;
  handleSortDateRequestedAt: () => void;
}

const configureFields = (tab: string[], sortOptions: SortOptionsType) => {
  const fields = [...workOrdersFields];

  fields.find((field) => field.name === 'start date')!.onSort =
    sortOptions.handleSortScheduledStartAt;
  fields.find((field) => field.name === 'end date')!.onSort = sortOptions.handleSortScheduledStopAt;
  fields.find((field) => field.name === 'date requested')!.onSort =
    sortOptions.handleSortDateRequestedAt;

  if (tab.length === 1) {
    fields.find((field) => field.name === 'action')!.hidden =
      tab[0] !== 'NEW' && tab[0] !== 'READY_TO_START';
    fields.find((field) => field.name === 'blank')!.hidden =
      tab[0] === 'NEW' || tab[0] === 'READY_TO_START';
    fields.find((field) => field.name === 'progress')!.hidden = tab[0] !== 'IN_PROGRESS';
  } else {
    fields.find((field) => field.name === 'blank')!.hidden = false;
    fields.find((field) => field.name === 'action')!.hidden = true;
    fields.find((field) => field.name === 'progress')!.hidden = true;
  }

  return fields;
};

export default configureFields;
